import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import AsyncSelect from "react-select/async";
import { getPdb } from "../../api/ApiService";
import { PdbEndpoint } from "../../config/pdbEndpoint";
import { MachineModel } from "../../interfaces/api/MachineModel";
import { SwitchViewMode } from "../../shared/enums/SwitchViewMode";
import { action } from "../../store/action";
import { MachineTonnage } from "../../store/interfaces/MachineTonnage";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import { useStateCleaner } from "../Products/hooks/useStateCleaner";
import "./MachineSearch.scss";

interface MachineSearchProps {
  currentMachineModel: MachineModel;
  hasSelectedProducts: boolean;
  onClick: React.MouseEventHandler<HTMLElement>;
}

const defaultMachineTonnage: MachineTonnage = {
  machineModel: undefined,
  switchViewMode: SwitchViewMode.MachineSearch,
  tonnage: undefined,
};

const MachineSearch = ({
  currentMachineModel,
  hasSelectedProducts,
  onClick,
}: MachineSearchProps) => {
  const { t } = useTranslation(["machinetonnagestep", "popup"]);
  const dispatch = useDispatch();
  const { clearStateAllProducts } = useStateCleaner();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [selectedMachineModel, setSelectedMachineModel] =
    useState<MachineModel>(currentMachineModel);
  const [previousdMachineModel, setPreviousdMachineModel] = useState(undefined);

  const loadOptions = (inputValue): Promise<MachineModel[]> => {
    if (inputValue.length === 0) {
      const promise = Promise.resolve();
      promise.then(() => {
        return [];
      });
    }

    return getPdb(`${PdbEndpoint.MachineSearch}/?search=${inputValue}`).then(
      (result) => {
        return result as MachineModel[];
      }
    );
  };

  const handleChange = (machineModel) => {
    if (selectedMachineModel) {
      setPreviousdMachineModel(selectedMachineModel);
    }

    setSelectedMachineModel(machineModel);

    if (selectedMachineModel && hasSelectedProducts) {
      setShowDialog(true);
      return;
    }

    const machineTonnage: MachineTonnage = {
      ...defaultMachineTonnage,
      machineModel,
    };
    dispatch(action.saveMachineTonnage(machineTonnage));
    dispatch(action.saveWizardEnableNext(true));
  };

  const onDialogClose = () => {
    if (previousdMachineModel) {
      setSelectedMachineModel(previousdMachineModel);
    }
    setShowDialog(false);
  };

  const changeMachineModel = () => {
    const machineTonnage: MachineTonnage = {
      ...defaultMachineTonnage,
      machineModel: selectedMachineModel,
    };

    dispatch(action.saveMachineTonnage(machineTonnage));
    clearStateAllProducts();
    setShowDialog(false);
  };

  return (
    <div className="machineSearchContainer">
      <div className="machineSearch">
        <div className="machineSearchHeader">
          <h2>{t("machinetonnagetexts.findmachine")}</h2>
          <div className="machineSearchSwitch" onClick={onClick}>
            <span>{t("machinetonnagetexts.switchtotonnage")}</span>
            <NavigateNextIcon />
          </div>
        </div>
        <div className="machineSearchInfo">
          {t("machinetonnagetexts.findmachinetext2")}{" "}
        </div>
        <div>
          <AsyncSelect
            className="dropDownSearch"
            placeholder={selectedMachineModel ? selectedMachineModel.name : t("machinetonnagetexts.noOptionsMessage")}
            value={selectedMachineModel}
            cacheOptions
            defaultOptions
            loadingMessage={() => t("machinetonnagetexts.loadingMessage")}
            noOptionsMessage={() => t("machinetonnagetexts.noOptionsMessage")}
            onChange={handleChange}
            loadOptions={loadOptions}
            getOptionLabel={(e) => e.name}
            getOptionValue={(e) => e.id.toString()}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: "#fbeeed",
                primary: "#db0f0f",
              },
            })}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                boxShadow: "0",
                borderRadius: "0",
                padding: "15px",
              }),
              option: (styles) => {
                return {
                  ...styles,
                  ":active": {
                    ...styles[":active"],
                    backgroundColor: "#fbeeed",
                  },
                };
              },
            }}
          />
          <ConfirmDialog
            dialogHeader={t("important", { ns: "popup" })}
            text={t("areyousure", { ns: "popup" })}
            okButtonText={t("clearchoises", { ns: "popup" })}
            cancelButtonText={t("cancel", { ns: "popup" })}
            open={showDialog}
            onClose={onDialogClose}
            onCancelClick={onDialogClose}
            onOkClick={changeMachineModel}
          ></ConfirmDialog>
        </div>
      </div>
    </div>
  );
};

export default MachineSearch;
